import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, Shield, Clock, Package, CheckCircle, 
  MapPin, Phone, ArrowLeft, Plane, Globe, Star,
  CreditCard, Headphones, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const DeliveryInfoPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: Package,
      title: 'Place Your Order',
      description: 'Order from our store or request any item from Dubai',
      image: 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      icon: Plane,
      title: 'We Source & Ship',
      description: 'We purchase your item in Dubai and ship it via express courier',
      image: 'https://images.pexels.com/photos/2026324/pexels-photo-2026324.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      icon: Shield,
      title: 'Customs Clearance',
      description: 'We handle all customs and import documentation seamlessly',
      image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      icon: CheckCircle,
      title: 'Doorstep Delivery',
      description: 'Receive your order at your Mumbai address in just 3 days',
      image: 'https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=400'
    }
  ];

  const features = [
    {
      icon: Clock,
      title: '3-Day Express Delivery',
      description: 'From Dubai to your doorstep in Mumbai within 3 working days',
      color: 'bg-blue-500'
    },
    {
      icon: Shield,
      title: '100% Authentic Products',
      description: 'All products sourced from authorized retailers in Dubai',
      color: 'bg-emerald-500'
    },
    {
      icon: Globe,
      title: 'Request Any Item',
      description: "Can't find it? We'll source and deliver any product from Dubai",
      color: 'bg-purple-500'
    },
    {
      icon: CreditCard,
      title: 'Secure Payments',
      description: 'Safe & secure payment options with buyer protection',
      color: 'bg-orange-500'
    },
    {
      icon: Package,
      title: 'Safe Packaging',
      description: 'Products carefully packed to ensure safe delivery',
      color: 'bg-pink-500'
    },
    {
      icon: Headphones,
      title: '24/7 Support',
      description: 'WhatsApp support for tracking and queries anytime',
      color: 'bg-teal-500'
    }
  ];

  const testimonials = [
    {
      name: 'Rahul M.',
      location: 'Mumbai',
      text: 'Got my iPhone 15 Pro from Dubai in just 3 days. Completely authentic with warranty!',
      rating: 5
    },
    {
      name: 'Priya S.',
      location: 'Pune',
      text: 'Ordered perfumes that were not available in India. DXB2BOM delivered perfectly.',
      rating: 5
    },
    {
      name: 'Amit K.',
      location: 'Bangalore',
      text: 'The on-demand service is amazing. They sourced a rare watch for me from Dubai.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Hero Section with Dubai Skyline */}
      <div className="relative overflow-hidden">
        {/* Background Image - Same as Dubai to India banner */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/32563053/pexels-photo-32563053.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Dubai Skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sky-900/95 via-blue-800/90 to-cyan-700/80" />
        </div>
        
        <div className="relative py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white/80 hover:text-white hover:bg-white/10 mb-6 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                  🚀 Express International Delivery
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  Dubai to India<br />
                  <span className="text-emerald-300">Door Delivery</span>
                </h1>
                <p className="text-lg text-white/90 mb-6 max-w-lg">
                  We deliver anything from Dubai to your doorstep in Mumbai within <span className="font-bold text-emerald-300">3 working days</span>. Trusted, reliable, and guaranteed.
                </p>
                
                <div className="flex flex-wrap gap-3 mb-8">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <Truck className="w-5 h-5 text-emerald-300" />
                    <span className="text-white text-sm font-medium">3-Day Delivery</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <Shield className="w-5 h-5 text-emerald-300" />
                    <span className="text-white text-sm font-medium">Fully Insured</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <CheckCircle className="w-5 h-5 text-emerald-300" />
                    <span className="text-white text-sm font-medium">Customs Handled</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => navigate('/products')}
                    size="lg"
                    className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shadow-xl"
                  >
                    Browse Products
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate('/request-item')}
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 font-semibold"
                  >
                    Request an Item
                  </Button>
                </div>
              </motion.div>

              {/* Floating Product Cards */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hidden md:block relative"
              >
                <div className="relative h-80">
                  <motion.div 
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute top-0 left-10 bg-white rounded-2xl shadow-2xl p-4 w-48"
                  >
                    <img src="https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&w=200" alt="iPhone" className="w-full h-28 object-cover rounded-lg mb-2" />
                    <p className="text-sm font-semibold text-slate-800">iPhone 15 Pro</p>
                    <p className="text-xs text-emerald-600">Delivered in 3 days</p>
                  </motion.div>
                  
                  <motion.div 
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                    className="absolute top-20 right-0 bg-white rounded-2xl shadow-2xl p-4 w-48"
                  >
                    <img src="https://images.pexels.com/photos/965989/pexels-photo-965989.jpeg?auto=compress&cs=tinysrgb&w=200" alt="Perfume" className="w-full h-28 object-cover rounded-lg mb-2" />
                    <p className="text-sm font-semibold text-slate-800">Dubai Perfumes</p>
                    <p className="text-xs text-emerald-600">100% Authentic</p>
                  </motion.div>
                  
                  <motion.div 
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                    className="absolute bottom-0 left-20 bg-white rounded-2xl shadow-2xl p-4 w-48"
                  >
                    <img src="https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=200" alt="Watch" className="w-full h-28 object-cover rounded-lg mb-2" />
                    <p className="text-sm font-semibold text-slate-800">Luxury Watches</p>
                    <p className="text-xs text-emerald-600">With warranty</p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-y border-slate-200 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-emerald-600">500+</p>
              <p className="text-sm text-slate-600">Products Delivered</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-600">3 Days</p>
              <p className="text-sm text-slate-600">Average Delivery</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-600">100%</p>
              <p className="text-sm text-slate-600">Authentic Products</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-600">24/7</p>
              <p className="text-sm text-slate-600">Customer Support</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works - Visual Timeline */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-3">How It Works</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">From order to doorstep - a seamless journey</p>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div 
                  key={`step-${step.step}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 h-full hover:shadow-xl transition-shadow">
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={step.image} 
                        alt={step.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                          <Icon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <span className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 text-emerald-300 z-10">
                      <ArrowRight className="w-6 h-6" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-16 px-4 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why Choose DXB2BOM?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Your trusted partner for Dubai to India deliveries</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={`feature-${feature.title.replace(/\s+/g, '-').toLowerCase()}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
                >
                  <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-3">What Our Customers Say</h2>
            <p className="text-slate-600">Real reviews from happy customers</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={`testimonial-${testimonial.name.replace(/\s+/g, '-').toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${testimonial.name}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-emerald-700 font-semibold">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <img 
              src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Shopping"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0047AB]/95 to-[#0066FF]/90" />
            <div className="relative p-10 md:p-16 text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Order from Dubai?</h2>
              <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto">
                Browse our collection or request any item. We'll deliver it to your doorstep!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/products')}
                  size="lg"
                  className="bg-white text-[#0047AB] hover:bg-white/90 font-semibold shadow-xl"
                >
                  Shop Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/request-item')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold"
                >
                  Request Custom Item
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="font-semibold text-slate-900 mb-1">Have Questions?</h3>
              <p className="text-sm text-slate-600">Our team is here to help 24/7</p>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://wa.me/971503847865" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-green-600 transition-colors"
              >
                <Phone className="w-4 h-4" />
                WhatsApp Us
              </a>
              <button 
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 bg-slate-100 text-slate-700 px-5 py-2.5 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                <MapPin className="w-4 h-4" />
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryInfoPage;
